import { defineStore } from 'pinia'
import { authApi } from '../services/api'
import { tokenService } from '../services/axios'
import { useToast, useColors } from 'vuestic-ui'
import router from '../router'
import i18n from '../i18n'
import type { User, LoginRequest } from '../types/api'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  loading: boolean
  permissions: string[] // Array de permisos del usuario
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    // Verificar si existe token al inicializar
    isAuthenticated: !!tokenService.getAccessToken(),
    loading: false,
    permissions: [], // Array de permisos vacío por defecto
  }),

  getters: {
    currentUser: (state) => state.user,
    isLoggedIn: (state) => state.isAuthenticated,
    userPermissions: (state) => state.permissions,
    userRole: (state) => {
      if (!state.user?.roles || state.user.roles.length === 0) return ''
      return state.user.roles[0]?.name || ''
    },

    // Verifica un permiso específico
    hasPermission:
      (state) =>
      (permission: string): boolean => {
        return state.permissions.includes(permission)
      },

    // Verifica múltiples permisos (OR - al menos uno)
    hasAnyPermission:
      (state) =>
      (permissions: string[]): boolean => {
        return permissions.some((p) => state.permissions.includes(p))
      },

    // Verifica múltiples permisos (AND - todos)
    hasAllPermissions:
      (state) =>
      (permissions: string[]): boolean => {
        return permissions.every((p) => state.permissions.includes(p))
      },

    // Helpers por módulo y acción
    canAccess:
      (state) =>
      (module: string): boolean => {
        return state.permissions.includes(`access.${module}`)
      },

    canView:
      (state) =>
      (module: string): boolean => {
        return state.permissions.includes(`view.${module}`)
      },

    canCreate:
      (state) =>
      (module: string): boolean => {
        return state.permissions.includes(`create.${module}`)
      },

    canUpdate:
      (state) =>
      (module: string): boolean => {
        return state.permissions.includes(`update.${module}`)
      },

    canDelete:
      (state) =>
      (module: string): boolean => {
        return state.permissions.includes(`delete.${module}`)
      },

    canRestore:
      (state) =>
      (module: string): boolean => {
        return state.permissions.includes(`restore.${module}`)
      },
  },

  actions: {
    async login(credentials: LoginRequest) {
      this.loading = true
      try {
        const response = await authApi.login(credentials)

        // Guardar tokens (access + refresh si están presentes)
        tokenService.setTokens(response.access_token, response.refresh_token ?? null)

        this.user = response.user
        this.isAuthenticated = true

        // Guardar permisos del usuario
        this.permissions = response.permissions || []
        // Guardar también en localStorage para persistencia
        localStorage.setItem('permissions', JSON.stringify(this.permissions))

        // Aplicar configuraciones del usuario (theme y language)
        this.applyUserPreferences(response.user)

        return response
      } catch (error) {
        this.user = null
        this.isAuthenticated = false
        throw error
      } finally {
        this.loading = false
      }
    },

    async logout(options: { skipApi?: boolean } = { skipApi: false }) {
      this.loading = true
      try {
        // Si no se indica skipApi, intentar notificar al servidor sobre el logout.
        if (!options.skipApi) {
          try {
            // Si ya existe un access token, usarlo para llamar al endpoint de logout.
            // Si no, intentar refrescar (si hay refresh token) y solo llamar al logout
            // si después del refresh hay un access token válido.
            let access = tokenService.getAccessToken()
            if (!access) {
              const refreshToken = tokenService.getRefreshToken()
              if (refreshToken) {
                try {
                  // Intentar renovar tokens
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                  await this.tryRefresh()
                } catch (e) {
                  // Ignorar errores de refresh
                }
                access = tokenService.getAccessToken()
              }
            }

            // Solo llamar al endpoint de logout si hay un access token disponible.
            if (access) {
              await authApi.logout()
            } else {
              // No hay token válido para autenticar la petición de logout en servidor.
              // Limpiamos localmente y redirigimos (no podemos notificar al servidor).
              // Esto evita llamadas que devuelvan 401 cuando el servidor exige Authorization.
            }
          } catch (error) {
            // Si el servidor responde 401 o falla, ignoramos porque vamos a limpiar localmente.
          }
        }
      } finally {
        // Limpiar estado local siempre
        this.user = null
        this.isAuthenticated = false
        this.permissions = []
        tokenService.clearTokens()
        localStorage.removeItem('permissions')
        this.loading = false

        // Redirigir siempre al login en el cliente (SPA navigation)
        try {
          await router.push('/auth/login')
        } catch (e) {
          // ignorar
        }
      }
    },

    // Intentar refrescar silenciosamente el access token usando el refresh token almacenado
    async tryRefresh() {
      this.loading = true
      try {
        const response = await authApi.refresh()
        if (response?.access_token) {
          tokenService.setTokens(response.access_token, response.refresh_token ?? null)
          this.user = response.user || this.user
          this.isAuthenticated = true
        }
        return response
      } catch (error) {
        // El refresh falló — permanecer desconectado
        this.user = null
        this.isAuthenticated = false
        this.permissions = []
        tokenService.clearTokens()
        localStorage.removeItem('permissions')
        try {
          const { init } = useToast()
          init({ message: 'La sesión ha sido invalidada. Inicia sesión de nuevo.', color: 'warning' })
        } catch (e) {
          void e
        }
        try {
          await router.push('/auth/login')
        } catch (e) {
          void e
        }
        // (Se removió el fallback forzado de replace/reload)
        return null
      } finally {
        this.loading = false
      }
    },

    async fetchCurrentUser() {
      if (!this.isAuthenticated) return

      this.loading = true
      try {
        const user = await authApi.me()
        this.user = user

        // Aplicar configuraciones del usuario
        this.applyUserPreferences(user)
      } catch (error) {
        // Si falló obtener el usuario, sólo forzar logout si el servidor devolvió 401
        const status = (error as any)?.response?.status
        if (status === 401) {
          try {
            await this.logout({ skipApi: true })
          } catch (e) {
            // ignorar
          }
        } else {
          // Para otros errores (p. ej. backend no disponible) no forzar logout automáticamente.
          // Dejar que la UI o la lógica de reintentos manejen la recuperación.
        }
      } finally {
        this.loading = false
      }
    },

    // Aplicar preferencias del usuario (theme y language)
    applyUserPreferences(user: User) {
      // Aplicar tema
      if (user.theme) {
        try {
          const { applyPreset } = useColors()
          applyPreset(user.theme)
          } catch (e) {
        }
      }

      // Aplicar idioma
      if (user.language) {
        try {
          i18n.global.locale.value = user.language
          } catch (e) {
        }
      }
    },

    // Cargar permisos desde localStorage al inicializar
    loadPermissionsFromStorage() {
      const permissionsStr = localStorage.getItem('permissions')
      if (permissionsStr) {
        try {
          this.permissions = JSON.parse(permissionsStr)
        } catch (e) {
          this.permissions = []
        }
      }
    },
  },
})
